// Hooks
import React, { useEffect, useState } from "react";

// Components
import Toast from "../../../Shared/toast/toast";
import ServiceInfoCards from "./service-info-cards/service-info-cards";
import ServicesPopup from "./services-popup/servicesPopup";
import ApprovalBox from "./approval-box/approvalBox";

// Style
import Styles from "./trial-center.module.scss";

// Functions
import { callApi } from "../../../api/fetch";
import {
  APPS_LIST_MAX_LIMIT,
  ensureArray,
  findTestAppId,
  unwrapListData,
  productARN,
  productName,
} from "../../../utils/dashboardMappers";
import { customEvent } from "../../../utils/ga4";

import Loading from "../../../Shared/loading/loading";
import { ACCENTCOLOR } from "../../../Shared/colors";
import { PlanType } from "../../../constants/orderTypes";

function TrialCenter() {
  const [showServicesPopup, setShowServicesPopup] = useState(false);
  const [isLoadingSubscribed, setIsLoadingSubscribed] = useState(true);
  const [selectedService, setSelectedService] = useState({});
  const [subscribedServices, setSubscribedServices] = useState<any>([]);
  const [pendingServices, setPendingServices] = useState<any>([]);
  const [isPostpaidClient, setIsPostpaidClient] = useState(false);

  const org_id: any = localStorage.getItem("organisation_id");
  const user: any = localStorage.getItem("@user");
  const x_user_id = JSON.parse(user)._id;
  const [appId, setAppId] = useState("");

  function checkHeaders(checks: string[]) {
    if (
      checks.includes("userID") &&
      (x_user_id === "" || x_user_id === undefined || x_user_id === null)
    ) {
      throw new Error(
        "Unable to retrieve verification results. Please try again later!"
      );
    }
    if (
      checks.includes("orgID") &&
      (org_id === "" || org_id === undefined || org_id === null)
    ) {
      throw new Error(
        "Unable to retrieve verification results. Please try again later!"
      );
    }
    if (
      checks.includes("appID") &&
      (appId === "" || appId === undefined || appId === null)
    ) {
      throw new Error(
        "Unable to retrieve verification results. Please try again later!"
      );
    }
  }

  async function getApp() {
    try {
      checkHeaders(["userID"]);
      const appsData = await callApi(`/apps?limit=${APPS_LIST_MAX_LIMIT}`, {
        method: "GET",
      });
      const apps = unwrapListData<Record<string, unknown>>(appsData, "apps");
      let applicationID =
        findTestAppId(apps) ||
        localStorage.getItem("test_app_id")?.trim() ||
        "";

      if (!applicationID) {
        try {
          const created = (await callApi("/apps", {
            method: "POST",
            data: { app_name: "Test App", test_app: true },
          })) as Record<string, unknown>;
          applicationID = (created.app_id as string) ?? "";
        } catch {
          const retryData = await callApi(`/apps?limit=${APPS_LIST_MAX_LIMIT}`, {
            method: "GET",
          });
          const retryApps = unwrapListData<Record<string, unknown>>(
            retryData,
            "apps"
          );
          applicationID =
            findTestAppId(retryApps) ||
            localStorage.getItem("test_app_id")?.trim() ||
            "";
        }
      }

      if (!applicationID) {
        throw new Error(
          "Unable to retrieve verification results. Please try again later!"
        );
      }
      setAppId(applicationID);
    } catch (e) {
      toastTimeout(
        true,
        "error",
        (e as Error).message ?? "Something went wrong"
      );
    }
  }

  function handleServicePopup(serviceInfo: Record<string, any>) {
    setShowServicesPopup(true);
    setSelectedService(serviceInfo);
    if (productName(serviceInfo)) {
      customEvent(
        "Run-Trial",
        `Run-Trial-Clicked-for-${productName(serviceInfo).split(" ").join("-")}`,
        `Run-Trial-for-${productName(serviceInfo).split(" ").join("-")}`
      );
    }
  }

  async function getAllSubscribedProducts() {
    try {
      setIsLoadingSubscribed(true);

      const response = ensureArray<any>(
        await callApi(
          "/products?subscription_status=subscribed&remaining_test_credits=true&display_on_trial=true",
          { method: "GET" }
        )
      );

      const newArr = response.filter((service: any) => {
        return (
          service?.display_on_trial === true &&
          productARN(service) &&
          productName(service)
        );
      });

      setSubscribedServices([...newArr]);
    } catch (e) {
      toastTimeout(
        true,
        "error",
        (e as Error).message ?? "Something went wrong"
      );
    } finally {
      setIsLoadingSubscribed(false);
    }
  }

  async function getAllUnsubscribedProducts() {
    try {
      const response = ensureArray<any>(
        await callApi(
          "/products?subscription_status=pending_approval",
          { method: "GET" }
        )
      );

      setPendingServices([...response]);
    } catch (e) {
      toastTimeout(
        true,
        "error",
        (e as Error).message ?? "Something went wrong"
      );
    }
  }

  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }

  useEffect(() => {
    getApp();
    getAllUnsubscribedProducts();
  }, []);

  useEffect(() => {
    if (!org_id) return;
    callApi(`/organisation`, { method: "GET" })
      .then((response) => {
        const t = response?.activePlan?.plan_type;
        setIsPostpaidClient(
          typeof t === "string" && t.toUpperCase() === PlanType.POSTPAID
        );
      })
      .catch(() => setIsPostpaidClient(false));
  }, [org_id]);

  useEffect(() => {
    getAllSubscribedProducts();
  }, []);

  return (
    <div className={Styles.container}>
      {isLoadingSubscribed ? (
        <div
          className="d-flex align-content-center justify-content-center"
          style={{ height: "calc(100vh - 70px)" }}
        >
          <Loading loadingColor={ACCENTCOLOR} />
        </div>
      ) : (
        <div className="p-4" style={{ height: "100%", minHeight: "100vh" }}>
          <div className="d-flex justify-content-between">
            <div className="d-flex">
              <div className="d-flex">
                <div className="flex-grow-1 ml-1">
                  <div>
                    <p className={`${Styles.headingText} ml-1`}>
                      Products available for trial
                    </p>

                    <div className="d-flex flex-wrap">
                      {subscribedServices.map((info: any, index: number) => (
                        <ServiceInfoCards
                          uniqueId={index}
                          key={index}
                          serviceInfo={info}
                          handleServicePopup={handleServicePopup}
                          browse={false}
                        />
                      ))}
                      <ServiceInfoCards browse={true} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              {pendingServices.length > 0 && (
                <div className="mt-2">
                  <ApprovalBox pendingServices={pendingServices} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showServicesPopup && (
        <ServicesPopup
          org_id={org_id}
          x_user_id={x_user_id}
          appId={appId}
          checkHeaders={checkHeaders}
          toastTimeout={toastTimeout}
          onCancel={() => setShowServicesPopup(false)}
          selectedService={selectedService}
          isPostpaidClient={isPostpaidClient}
        />
      )}
    </div>
  );
}

export default TrialCenter;
